import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import SearchInput from '../../ui/SearchInput'
import IssueResponseSection from './IssueResponseSection'
import GlobalGradeCard from './GlobalGradeCard'
import { TRIGGER_STATE_SHAPE } from './TriggerStates'

const SECTION_ID = 'section_id'
const TRIGGERED_GRADE_KEY = 'triggered_grade'
const TRIGGER_STATES_KEY = 'trigger_states'

const WRAPPER_CLASS = 'tw:flex tw:flex-col tw:gap-4'
const HEAD_CLASS = 'tw:flex tw:items-center tw:gap-4'
const TOGGLE_ALL_CLASS = 'tw:bg-transparent tw:border-0 tw:p-0 tw:cursor-pointer '
  + 'tw:text-sm tw:text-gray-900 tw:underline! tw:whitespace-nowrap'
const EMPTY_CLASS = 'tw:text-sm tw:text-gray-500 tw:py-8 tw:text-center'

const EMPTY_RESULTS = []
const NO_RESULT = 'Aucun résultat à afficher pour cette ressource.'
const NO_MATCH = 'Aucune question ne correspond à cette recherche.'

const matches = (question, needle) =>
  `${question.title || ''} ${question.value || ''}`.toLowerCase().includes(needle)

const filterSections = (results, search) => {
  const needle = search.trim().toLowerCase()
  if (!needle)
    return results
  return results
    .map(section => ({
      ...section,
      questions: section.questions.filter(question => matches(question, needle))
    }))
    .filter(section => section.questions.length > 0)
}

const loneSectionIds = results => results.length === 1 ? [results[0][SECTION_ID]] : EMPTY_RESULTS

const hasTriggers = question => question[TRIGGER_STATES_KEY]?.length > 0

const triggerCountOf = section => section.questions.filter(hasTriggers).length
  + (section[TRIGGERED_GRADE_KEY] ? 1 : 0)

const renderGlobalGrade = globalGrade => {
  if (!globalGrade)
    return null
  return (
    <GlobalGradeCard
      grade={globalGrade.grade}
      min={globalGrade.min}
      size={globalGrade.size}
      states={globalGrade.states}
    />
  )
}

const triggerCountsBySection = results => new Map(results.map(section => [
  section[SECTION_ID],
  triggerCountOf(section)
]))

// The drawer owns the open sections so that they outlive a switch to another tab.
const IssueResponseTab = ({
  results = EMPTY_RESULTS, openIds: storedOpenIds = null, onOpenIdsChange, globalGrade = null
}) => {
  const [search, setSearch] = useState('')
  const [searchOpenIds, setSearchOpenIds] = useState(EMPTY_RESULTS)
  const searching = search.trim() !== ''
  // A search unfolds its matches for itself only, so the folds kept by the drawer survive it.
  const openIds = searching ? searchOpenIds : storedOpenIds || loneSectionIds(results)
  const changeOpenIds = searching ? setSearchOpenIds : onOpenIdsChange

  const sections = useMemo(() => filterSections(results, search), [results, search])
  const triggerCounts = useMemo(() => triggerCountsBySection(results), [results])
  const allOpen = sections.length > 0 && sections.every(section => openIds.includes(section[SECTION_ID]))

  useEffect(() => {
    if (search.trim())
      setSearchOpenIds(sections.map(section => section[SECTION_ID]))
  }, [sections, search])

  const handleSearch = useCallback(event => setSearch(event.target.value), [])

  const handleToggleAll = useCallback(() => {
    changeOpenIds(allOpen ? [] : sections.map(section => section[SECTION_ID]))
  }, [allOpen, sections, changeOpenIds])

  const handleToggle = useCallback(sectionId => {
    if (openIds.includes(sectionId))
      changeOpenIds(openIds.filter(openId => openId !== sectionId))
    else
      changeOpenIds([...openIds, sectionId])
  }, [openIds, changeOpenIds])

  if (results.length === 0)
    return <p className={EMPTY_CLASS}>{NO_RESULT}</p>

  return (
    <div className={WRAPPER_CLASS}>
      {renderGlobalGrade(globalGrade)}
      <div className={HEAD_CLASS}>
        <SearchInput
          className="tw:flex-1"
          name="issue-response-search"
          placeholder="Rechercher..."
          value={search}
          onChange={handleSearch}
        />
        <button className={TOGGLE_ALL_CLASS} type="button" onClick={handleToggleAll}>
          {allOpen ? 'Tout fermer' : 'Tout ouvrir'}
        </button>
      </div>
      {sections.length === 0 ? <p className={EMPTY_CLASS}>{NO_MATCH}</p> : null}
      {sections.map(section => (
        <IssueResponseSection
          key={section[SECTION_ID]}
          isOpen={openIds.includes(section[SECTION_ID])}
          section={section}
          triggerCount={triggerCounts.get(section[SECTION_ID])}
          onToggle={handleToggle}
        />
      ))}
    </div>
  )
}

IssueResponseTab.propTypes = {
  globalGrade: PropTypes.shape({
    grade: PropTypes.number.isRequired,
    min: PropTypes.number.isRequired,
    size: PropTypes.number.isRequired,
    states: PropTypes.arrayOf(TRIGGER_STATE_SHAPE).isRequired
  }),
  onOpenIdsChange: PropTypes.func.isRequired,
  openIds: PropTypes.arrayOf(PropTypes.number),
  results: PropTypes.arrayOf(PropTypes.shape({
    [SECTION_ID]: PropTypes.number,
    questions: PropTypes.arrayOf(PropTypes.shape({}))
  }))
}

IssueResponseTab.defaultProps = {
  globalGrade: null,
  openIds: null,
  results: EMPTY_RESULTS
}

export default IssueResponseTab
